import React, { useState } from "react";
import { Box, Drawer, IconButton, Snackbar, Toolbar, Tooltip } from "@mui/material";
import { Close as CloseIcon, Link as LinkIcon, OpenInNew } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTaskOverlay } from "../../utils/TaskOverlayContext";
import TaskDetailsPage from "./TaskDetails";

/**
 * Right-side drawer host for the task detail surface in "drawer" display mode.
 * Renders the same {@link TaskDetailsPage} content as the full page route, just
 * fed a `taskId` prop instead of a route param. Covers ~60% of the viewport so
 * the originating list stays visible underneath.
 */
const TaskDetailDrawer = () => {
  const { displayMode, detailTaskId, closeTaskDetail } = useTaskOverlay();
  const navigate = useNavigate();
  const [linkCopied, setLinkCopied] = useState(false);

  // Render in drawer mode, or whenever a task has been pushed into the drawer
  // from the full-page route ("Show as drawer") even while the saved mode is
  // "page".
  if (displayMode !== "drawer" && detailTaskId == null) return null;

  const openFullPage = () => {
    if (detailTaskId == null) return;
    closeTaskDetail();
    navigate(`/tasks/${detailTaskId}`);
  };

  const handleCopyLink = async () => {
    if (detailTaskId == null) return;
    const url = `${window.location.origin}/tasks/${detailTaskId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for browsers / contexts without the async clipboard API.
      const el = document.createElement("textarea");
      el.value = url;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setLinkCopied(true);
  };

  return (
    <Drawer
      anchor="right"
      open={detailTaskId != null}
      onClose={closeTaskDetail}
      PaperProps={{ sx: { width: { xs: "100%", md: "60%" } } }}
    >
      {/* Spacer so the drawer clears the fixed AppBar (zIndex: drawer + 1). */}
      <Toolbar />
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderBottom: 1,
          borderColor: "divider",
          position: "sticky",
          top: theme.mixins.toolbar.minHeight,
          zIndex: 1,
          bgcolor: "background.paper",
        })}
      >
        <Tooltip title="Copy link to task">
          <IconButton size="small" onClick={handleCopyLink} aria-label="Copy link to task">
            <LinkIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Open as full page">
          <IconButton size="small" onClick={openFullPage}>
            <OpenInNew fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Close">
          <IconButton size="small" onClick={closeTaskDetail}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {detailTaskId != null && <TaskDetailsPage taskId={detailTaskId} />}
      <Snackbar
        open={linkCopied}
        autoHideDuration={2000}
        onClose={() => setLinkCopied(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Drawer>
  );
};

export default TaskDetailDrawer;
